import React from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { token } from '@atlaskit/tokens';
import { ButtonProps, ButtonSize } from '../index';
import { getLinkButtonSpacingStyles } from '@components/shared/Button/utils/buttonStyleHelpers';

interface LinkButtonProps extends ButtonProps {
  clickHandler: (e: React.MouseEvent<HTMLElement>) => void;
  testId: string;
  showBeforeIcon: React.ReactElement | false;
  showAfterIcon: React.ReactElement | false;
  isButtonWithoutIcon: boolean;
}

const getLinkButtonStyles = (disabled: boolean) => [
  learningButtonLinkStyles['basic'],
  disabled && learningButtonLinkStyles['disabled'],
];

export const LinkButton: React.FC<LinkButtonProps> = ({
  label,
  disabled = false,
  clickHandler,
  testId,
  size = ButtonSize.MEDIUM,
  href,
  target,
  withPadding = true,
  showBeforeIcon,
  showAfterIcon,
  isButtonWithoutIcon,
}) => {
  const commonProps = {
    'aria-label': label.replaceAll('-', ' '),
    'data-testid': testId,
    css: [
      ...getLinkButtonStyles(disabled),
      ...getLinkButtonSpacingStyles(size, isButtonWithoutIcon, showBeforeIcon, showAfterIcon, withPadding),
    ],
  };

  const content = (
    <>
      {showBeforeIcon && (
        <span css={[buttonIconContainerStyles, css({ marginRight: token('space.050') })]}>{showBeforeIcon}</span>
      )}
      <span>{label}</span>
      {showAfterIcon && (
        <span
          css={[buttonIconContainerStyles, css({ marginLeft: token('space.050') }), disabled && disabledIconStyles]}
        >
          {showAfterIcon}
        </span>
      )}
    </>
  );

  if (href && !disabled) {
    return (
      <a
        {...commonProps}
        href={href}
        target={target}
        rel={target === '_blank' ? 'noreferrer' : ''}
        onClick={clickHandler}
      >
        {content}
      </a>
    );
  }

  return (
    <button {...commonProps} disabled={disabled} onClick={clickHandler}>
      {content}
    </button>
  );
};

const learningButtonLinkStyles = {
  basic: css({
    display: 'flex',
    width: 'fit-content',
    padding: `10px ${token('space.150')}`,
    border: 'none',
    alignItems: 'center',
    fontFamily: 'Charlie Text, sans-serif',
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '20px',
    color: token('color.text.brand'),
    background: 'transparent',
    outline: 'none',
    cursor: 'pointer',

    '&:hover': {
      color: token('color.text.brand'),
      textDecoration: 'underline',
      textUnderlineOffset: '4px',
    },

    '&:active': {
      color: token('color.text.brand'),
      textDecoration: 'underline',
    },
  }),

  disabled: css({
    color: token('color.text.disabled'),

    '&:hover': {
      textDecoration: 'none',
    },

    '&:active': {
      textDecoration: 'none',
    },
  }),
} as { [key: string]: SerializedStyles };

const buttonIconContainerStyles = css({
  width: '24px',
  height: '24px',
});

const disabledIconStyles = css({
  color: token('color.icon.disabled'),
  cursor: 'not-allowed',
});
